/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import ApiErrorsService from 'cio/services/api-errors';
import ArrayProxy from '@ember/array/proxy';
import DS from 'ember-data';
import TagModel from 'cio/models/tag';
import TagsService from 'cio/services/tags';

interface TagEditorArgs {
  dropdownClass?: string;
  tags?: DS.PromiseManyArray<TagModel> | TagModel[];
  editOnly: boolean;
  readOnly: boolean;
  hideTagsHeader: boolean;
  renderInPlace?: boolean;
  onAddTag: (tag: TagModel) => unknown;
  onRemoveTag: (tag: TagModel) => unknown;
}

export default class TagEditor extends Component<TagEditorArgs> {
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service tags!: TagsService;

  @tracked environmentTags: TagModel[] | ArrayProxy<TagModel> = [];
  @tracked isAdding = false;
  @tracked showTagModal = false;

  get renderInPlace() {
    return this.args.renderInPlace ?? false;
  }

  get dropdownOptions() {
    return (this.environmentTags ?? []).map((tag) => {
      return {
        value: tag.id,
        label: tag.name,
      };
    });
  }

  get tagsArray() {
    return ((this.args.tags as DS.PromiseManyArray<TagModel>)?.content as TagModel[]) ?? this.args.tags;
  }

  get selectedValues() {
    return this.tagsArray.map((tag) => tag.id);
  }

  @action
  openTagModal() {
    this.showTagModal = true;
  }

  @action
  closeModal() {
    this.showTagModal = false;
    this.getEnvironmentTagsTask.perform();
  }

  @action
  updateTags(selectedTags: Record<string, any>[]) {
    let { tagsArray } = this;

    if (tagsArray == null) {
      return;
    }

    if (selectedTags.length < tagsArray?.length) {
      // Removing a tag
      let toRemove = tagsArray.find((tag) => {
        return !selectedTags.find((selectedTag) => selectedTag.value === tag.id);
      });

      if (!toRemove) {
        return;
      }

      return this.removeTagTask.perform(toRemove);
    } else {
      // Adding new tags
      let toAdd = selectedTags.find((selectedTag) => {
        return !tagsArray?.find((tag) => tag.id === selectedTag.value);
      });

      if (!toAdd) {
        return;
      }

      return this.addTagTask.perform(toAdd);
    }
  }

  addTagTask = task(async (tagOption: Record<string, any>) => {
    await this.args.onAddTag(await this.tags.addTag(tagOption.label));
    await this.getEnvironmentTagsTask.perform();
  });

  getEnvironmentTagsTask = task(async () => {
    this.environmentTags = await this.tags.loadTags();
  });

  removeTagTask = task(async (tag: TagModel) => {
    await this.args.onRemoveTag(tag);
  });

  constructor(owner: unknown, args: TagEditorArgs) {
    super(owner, args);
    this.getEnvironmentTagsTask.perform();
  }
}
